/**
 * Special orders: items out of stock at the manufacturer (variant metafield daro.vendor_stock, see
 * snippets/daro-special-order.liquid) can only be bought once the buyer ticks the non-cancellable acknowledgement.
 *
 * Product page (blocks/daro-availability): product-form.js won't add the item until [data-daro-ack] is ticked; here,
 * the form's Buy it now / wallet buttons stay locked (inert) until then, as they skip the add-to-cart handler.
 *
 * Cart page and drawer (<daro-special-order-gate>, snippets/daro-cart-special-order): lines that reached the cart without
 * the product page's acknowledgement (quick add from a card, an older cart) gate the checkout button and express
 * buttons in the gate's parent element, like the terms checkbox (cart-terms.js). Ticked, the answer is kept at module
 * level, so it survives the drawer and the cart summary re-rendering, and saved on the cart as an attribute (in the order).
 */
(() => {
  if (window.DaroSpecialOrder) return;

  // ---- Product page: Buy it now waits for the acknowledgement ----
  const syncProductForms = () => {
    document.querySelectorAll('[data-daro-ack][form]').forEach((ack) => {
      const form = document.getElementById(ack.getAttribute('form'));
      const wallet = form?.querySelector('.shopify-payment-button');
      if (!wallet) return;
      wallet.classList.toggle('is-locked', !ack.checked);
      Array.from(wallet.children).forEach((child) => child.toggleAttribute('inert', !ack.checked));
      wallet.dataset.daroAckLocked = '';
    });
    // A variant without the notice: unlock what an earlier one locked
    document.querySelectorAll('.shopify-payment-button[data-daro-ack-locked]').forEach((wallet) => {
      const form = wallet.closest('form');
      if (form && document.querySelector(`[data-daro-ack][form="${form.getAttribute('id')}"]`)) return;
      wallet.classList.remove('is-locked');
      Array.from(wallet.children).forEach((child) => child.removeAttribute('inert'));
      delete wallet.dataset.daroAckLocked;
    });
  };
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-daro-ack]')) syncProductForms();
  });
  // The block is re-rendered on variant change (daro-segments.js swaps it after variant:update)
  document.addEventListener('variant:update', () => setTimeout(syncProductForms, 0));
  // A click on a locked wallet lands on its wrapper: say why
  document.addEventListener(
    'click',
    (event) => {
      const wallet = event.target.closest?.('.shopify-payment-button.is-locked');
      if (!wallet) return;
      const form = wallet.closest('form');
      const ack = form && document.querySelector(`[data-daro-ack][form="${form.getAttribute('id')}"]`);
      if (!ack || ack.checked) return;
      event.preventDefault();
      event.stopPropagation();
      form.closest('product-form-component')?.handleErrorMessage?.(ack.dataset.daroAckError);
      ack.focus();
    },
    true
  );
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncProductForms);
  else syncProductForms();
  // Wallet buttons render late (Shopify's script fills .shopify-payment-button)
  window.addEventListener('load', syncProductForms);

  // ---- Cart page and drawer ----
  let cartAcknowledged = false;

  class DaroSpecialOrderGate extends HTMLElement {
    connectedCallback() {
      const scope = this.parentElement;
      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.error = this.querySelector('.daro-so-gate__error');
      this.checkoutButton = scope?.querySelector('.cart__checkout-button');
      this.expressButtons = scope?.querySelector('.cart__dynamic-checkout-buttons');
      if (!this.checkbox || !this.checkoutButton) return;
      this.onChange = () => {
        cartAcknowledged = this.checkbox.checked;
        this.sync();
        if (cartAcknowledged) this.save();
      };
      this.onBlockedClick = (event) => {
        if (cartAcknowledged) return;
        event.preventDefault();
        event.stopPropagation();
        this.error.hidden = false;
        this.checkbox.focus();
      };
      this.checkbox.checked = cartAcknowledged;
      this.checkbox.addEventListener('change', this.onChange);
      this.checkoutButton.addEventListener('click', this.onBlockedClick);
      this.expressButtons?.addEventListener('click', this.onBlockedClick, true);
      this.sync();
    }

    disconnectedCallback() {
      this.checkbox?.removeEventListener('change', this.onChange);
      this.checkoutButton?.removeEventListener('click', this.onBlockedClick);
      this.expressButtons?.removeEventListener('click', this.onBlockedClick, true);
    }

    sync() {
      const locked = !cartAcknowledged;
      // The terms checkbox (cart-terms.js) may lock the same button: only unlock when it's ticked too
      const terms = this.parentElement?.querySelector('cart-terms input[type="checkbox"]');
      this.checkoutButton.setAttribute('aria-disabled', String(locked || (terms ? !terms.checked : false)));
      if (this.expressButtons) {
        this.expressButtons.classList.toggle('is-locked', locked);
        Array.from(this.expressButtons.children).forEach((child) => child.toggleAttribute('inert', locked));
      }
      if (!locked) this.error.hidden = true;
    }

    // On the cart, so it's in the order's "Additional details" too
    save() {
      const value = this.dataset.attribute;
      if (!value) return;
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Special order acknowledged': `${value} (${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC)` } }),
      }).catch(() => {});
    }
  }
  if (!customElements.get('daro-special-order-gate')) customElements.define('daro-special-order-gate', DaroSpecialOrderGate);

  window.DaroSpecialOrder = { syncProductForms };
})();
